<template>
  <div>

    <!-- Disclaimer -->
    <div class="mt-8 bg-layer-2 rounded-xl border border-stroke-separator p-4 flex items-start gap-3">
      <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" class="text-elements-blue shrink-0 mt-0.5">
        <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="1.5" />
        <path d="M10 9v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        <circle cx="10" cy="6.5" r="1" fill="currentColor" />
      </svg>
      <p class="text-sm text-text-neutral-medium">
        Numbers can't be used in the Term field — they must be spelled out as words, for example "one" instead of "1" or "one-two-three" instead of "123".<br />
        Spaces between words will be replaced with "-" when saved.<br />
        For acronyms, use dots — e.g. "A.S.R." for "Automatic Speech Recognition".<br />
        Allowed characters depend on the Term's language — unsupported symbols will be rejected.
      </p>
    </div>

    <!-- Sync in progress -->
    <div v-if="isVocabSyncing" class="mt-4 bg-yellow-300 rounded-xl border border-yellow-500/40 p-4 flex items-center gap-3">
      <span class="inline-block w-4 h-4 border-2 border-gray-900 border-t-transparent rounded-full animate-spin shrink-0"></span>
      <p class="text-sm text-gray-900">
        Vocabulary list is syncing. Editing is temporarily disabled until the sync finishes.
      </p>
    </div>

    <!-- Add words + table -->
    <div class="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
      <!-- Left column -->
      <div class="flex flex-col gap-6">
        <!-- Bulk import -->
        <div class="bg-layer-2 rounded-xl border border-stroke-separator p-6 flex flex-col">
          <div class="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 class="text-base font-medium text-text-neutral-extreme">Bulk Import</h2>
              <p class="text-sm text-text-neutral-medium mt-1">Populate vocabulary from file</p>
            </div>
            <a
                href="/data/example.csv"
                download="example.csv"
                class="text-sm text-elements-blue hover:underline shrink-0"
            >
              Example file for import
            </a>
          </div>

          <div
              class="relative flex-1 rounded-xl border-2 border-dashed p-8 flex flex-col items-center justify-center text-center transition-colors"
              :class="[
                bulkImport.file ? 'cursor-default' : 'cursor-pointer',
                bulkImportBorderClass,
                isVocabSyncing ? 'opacity-50 pointer-events-none' : ''
              ]"
              @click="onDropzoneClick"
              @dragover.prevent="dragOver = true"
              @dragleave.prevent="dragOver = false"
              @drop.prevent="onFileDrop"
          >
            <input ref="csvInputRef" type="file" accept=".csv,text/csv" class="hidden" @change="onFileInputChange" />

            <template v-if="!bulkImport.file">
              <img src="@/assets/icons/cloud-upload.svg" class="w-12 h-12 mb-4" alt="" />
              <div class="text-text-neutral-extreme font-medium mb-1">Click to upload CSV or drag and drop</div>
              <div class="text-sm text-text-neutral-medium">Must contain one word per line</div>
            </template>

            <div v-else class="w-full flex items-start gap-3 text-left" @click.stop>
              <div class="w-10 h-10 rounded-lg bg-elements-blue/10 flex items-center justify-center shrink-0">
                <img src="@/assets/icons/document.svg" class="w-5 h-5" alt="" />
              </div>
              <div class="flex-1 min-w-0">
                <div class="flex items-center justify-between gap-3">
                  <span class="text-text-neutral-extreme font-medium truncate">{{ bulkImport.file.name }}</span>
                  <button class="text-text-neutrals-placeholder hover:text-text-neutrals-primary cursor-pointer shrink-0" @click="resetBulkImport">
                    <img src="@/assets/icons/xmark.svg" class="w-3 h-3" alt="Remove" />
                  </button>
                </div>

                <div class="flex items-center gap-1.5 text-sm mt-1" :class="bulkImportStatusTextClass">
                  <span v-if="bulkImport.status === 'uploading'" class="inline-block w-3.5 h-3.5 border-2 border-elements-blue border-t-transparent rounded-full animate-spin"></span>
                  <img v-if="bulkImport.status === 'success'" src="@/assets/icons/check-circle-green.svg" class="w-3.5 h-3.5" alt="" />
                  <img v-if="bulkImport.status === 'error'" src="@/assets/icons/error-circle-red.svg" class="w-3.5 h-3.5" alt="" />
                  {{ bulkImportStatusText }}
                </div>

                <div class="mt-3 h-1.5 w-full rounded-full bg-layer-2 overflow-hidden">
                  <div class="h-full rounded-full transition-all duration-150" :class="bulkImportBarClass" :style="{ width: bulkImport.progress + '%' }"></div>
                </div>

                <div v-if="bulkImport.status === 'uploading'" class="flex items-center justify-between text-xs text-text-neutral-medium mt-1.5">
                  <span>{{ bulkImport.progress }}% completed</span>
                  <span>{{ formatBytes(bulkImport.loadedBytes) }} / {{ formatBytes(bulkImport.file.size) }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Add single word -->
        <div class="bg-layer-2 rounded-xl border border-stroke-separator p-6">
          <h2 class="text-base font-medium text-text-neutral-extreme mb-4">Add Individual Word</h2>

          <form @submit.prevent="addWord" :class="isVocabSyncing ? 'opacity-50 pointer-events-none' : ''">
            <div class="mb-4">
              <language-select v-model="form.language" />
            </div>

            <div class="mb-4">
              <label class="block text-sm text-text-neutral-medium mb-2">Term</label>
              <input
                  v-model="form.term"
                  type="text"
                  required
                  placeholder="e.g. A.I."
                  class="w-full h-11 px-3 border rounded-lg bg-transparent text-text-neutral-extreme placeholder-text-neutrals-placeholder outline-none"
                  :class="(termHasDigits || termInvalidNonDigitChars.length) ? 'border-elements-red focus:border-elements-red' : 'border-stroke-separator focus:border-elements-blue'"
              />
              <p v-if="termHasDigits" class="mt-2 text-sm text-elements-red">
                Numbers can't be used in this field. Please spell them out instead, e.g. "{{ termDigitsAsWords }}" instead of "{{ termDigits }}".
              </p>
              <p v-else-if="termInvalidNonDigitChars.length" class="mt-2 text-sm text-elements-red">
                These characters aren't supported for {{ getLanguageName(form.language) }}: "{{ termInvalidNonDigitChars.join('", "') }}".
              </p>
            </div>

            <div class="mb-5">
              <label class="block text-sm text-text-neutral-medium mb-2">Displayed as</label>
              <input
                  v-model="form.displayed_as"
                  type="text"
                  placeholder="e.g. Artificial Intelligence"
                  class="w-full h-11 px-3 border border-stroke-separator rounded-lg bg-transparent text-text-neutral-extreme placeholder-text-neutrals-placeholder outline-none focus:border-elements-blue"
              />
            </div>

            <button
                type="submit"
                :disabled="!canSubmit || loading"
                class="w-full h-11 rounded-lg text-sm font-medium transition-all duration-200"
                :class="[
                  canSubmit && !loading ? 'bg-elements-blue hover:bg-opacity-80 text-black cursor-pointer' : 'bg-layer-3 text-text-neutral-medium cursor-default',
                  justEnabled ? 'add-btn-pop' : ''
                ]"
            >
              Add
            </button>
          </form>
        </div>
      </div>

      <!-- Words table -->
      <div class="lg:col-span-2 bg-layer-2 rounded-xl border border-stroke-separator overflow-hidden">

      <!-- Toolbar -->
      <div class="flex items-center justify-between gap-4 px-6 py-4 border-b border-stroke-separator">
        <div class="flex items-center gap-1 bg-layer-0 border border-stroke-separator rounded-lg p-1 shrink-0">
          <button
              v-for="option in sortOptions"
              :key="option.value"
              type="button"
              class="px-3 h-8 rounded-md text-sm font-medium transition-colors cursor-pointer"
              :class="sortMode === option.value ? 'bg-elements-blue text-black' : 'text-text-neutral-medium hover:text-text-neutral-extreme'"
              @click="sortMode = option.value"
          >
            {{ option.label }}
          </button>
        </div>

        <div class="flex items-center gap-4">
          <template v-if="hasUnsavedChanges">
            <button
                type="button"
                :disabled="saving || isVocabSyncing"
                class="px-5 h-9 rounded-lg text-sm font-medium bg-elements-blue hover:bg-opacity-80 text-black transition-all duration-200 cursor-pointer disabled:opacity-60 disabled:cursor-default add-btn-pop"
                @click="saveChanges"
            >
              {{ saving ? 'Saving...' : 'Save' }}
            </button>

            <div class="w-px h-6 bg-stroke-separator"></div>
          </template>

          <div class="relative">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="absolute left-3 top-1/2 -translate-y-1/2 text-text-neutrals-placeholder pointer-events-none">
              <circle cx="7" cy="7" r="5.5" stroke="currentColor" stroke-width="1.5" />
              <path d="M14 14L11.5 11.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
            </svg>
            <input
                v-model="searchQuery"
                type="text"
                placeholder="Search..."
                class="h-9 pl-9 pr-3 w-48 rounded-lg border border-stroke-separator bg-transparent text-sm text-text-neutral-extreme placeholder-text-neutrals-placeholder outline-none focus:border-elements-blue"
            />
          </div>

          <button
              v-if="selectedIds.size > 0"
              class="px-5 h-9 rounded-lg text-sm font-medium transition-all duration-200"
              :class="isVocabSyncing ? 'bg-layer-2 text-text-neutral-medium cursor-default' : 'bg-elements-blue hover:bg-opacity-80 text-black cursor-pointer'"
              :disabled="isVocabSyncing"
              @click="requestDeleteSelected"
          >
            Delete selected
          </button>
        </div>
      </div>

      <!-- Loading -->
      <div v-if="loading" class="px-6 py-8 text-sm text-text-neutral-medium">
        Loading vocabulary...
      </div>

      <!-- Table -->
      <div v-else class="relative w-full overflow-x-auto">
        <table class="min-w-[900px] w-full text-sm table-fixed">
          <colgroup>
            <col style="width: 49%" />
            <col style="width: 33%" />
            <col style="width: 18%" />
          </colgroup>

          <thead class="border-b border-stroke-separator text-text-neutral-medium text-xs uppercase">
          <tr>
            <th class="text-left py-4 pl-6 pr-3 font-bold">Language / Word / Abbreviation</th>
            <th class="text-left py-4 px-3 font-bold">Displayed</th>
            <th class="text-left py-4 px-3 font-bold">Actions</th>
          </tr>
          </thead>

          <tbody class="divide-y divide-stroke-separator">
          <tr v-for="term in visibleTerms" :key="term.id">
            <!-- Language / Word / Abbreviation -->
            <td class="py-4 pl-6 pr-3 relative">
              <div class="flex items-stretch gap-3">
                <div v-if="term.pending" class="w-1 rounded-full bg-elements-blue shrink-0"></div>
                <div class="min-w-0 flex-1">
                  <template v-if="editingId === term.id">
                    <input
                        v-model="editDraft.text"
                        type="text"
                        class="w-full h-9 px-2 border rounded-lg bg-transparent text-text-neutral-extreme outline-none"
                        :class="(editHasDigits || editInvalidNonDigitChars.length) ? 'border-elements-red focus:border-elements-red' : 'border-stroke-separator focus:border-elements-blue'"
                    />
                    <p v-if="editHasDigits" class="text-xs text-elements-red mb-2">
                      Numbers aren't allowed — spell them out, e.g. "one" instead of "1".
                    </p>
                    <p v-else-if="editInvalidNonDigitChars.length" class="text-xs text-elements-red mb-2">
                      Unsupported for {{ getLanguageName(editDraft.language) }}: "{{ editInvalidNonDigitChars.join('", "') }}".
                    </p>

                    <button
                        ref="editLanguageBtnRef"
                        type="button"
                        class="flex items-center gap-2 w-full max-w-[220px] h-9 px-2 border border-stroke-separator rounded-lg bg-transparent text-text-neutral-extreme outline-none focus:border-elements-blue cursor-pointer"
                        @click="toggleEditLanguageSelector"
                    >
                      <img :src="`/data/flags/${editDraft.language}.png`" :alt="editDraft.language" class="h-5 w-auto rounded-[2px] shrink-0" />
                      <span class="text-sm truncate flex-1 text-left">{{ getLanguageName(editDraft.language) }}</span>
                      <img src="@/assets/icons/chevron-down-white.svg" class="w-3 h-3 shrink-0" alt="" />
                    </button>

                    <Teleport to="body">
                      <div v-if="editLanguageSelectorOpen" class="fixed inset-0 z-40" @click="editLanguageSelectorOpen = false"></div>
                      <div
                          v-if="editLanguageSelectorOpen"
                          class="fixed z-50 w-[320px] bg-layer-2 border border-stroke-separator rounded-lg shadow-lg p-3"
                          :style="editDropdownStyle"
                      >
                        <language-picker :initial="editDraft.language" :close-on-select="false" @select="onSelectEditLanguage"></language-picker>
                      </div>
                    </Teleport>
                  </template>
                  <template v-else>
                    <div class="font-medium text-text-neutral-extreme truncate">{{ term.text }}</div>
                    <div class="text-xs text-text-neutral-medium mt-0.5 truncate">{{ getLanguageName(term.language) }}</div>
                  </template>
                </div>
              </div>
            </td>

            <!-- Displayed as -->
            <td class="py-4 px-3">
              <input
                  v-if="editingId === term.id"
                  v-model="editDraft.displayed_as"
                  type="text"
                  placeholder="same as Term if not specified"
                  class="w-full h-9 px-2 border border-stroke-separator rounded-lg bg-transparent text-text-neutral-extreme placeholder-text-neutrals-placeholder outline-none focus:border-elements-blue"
              />
              <template v-else>{{ term.displayed_as }}</template>
            </td>

            <!-- Actions -->
            <td class="py-4 px-3">
              <div class="flex items-center gap-3">
                <template v-if="editingId === term.id">
                  <button
                      class="flex items-center gap-1.5 text-text-neutral-extreme hover:text-elements-blue cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:text-text-neutral-extreme"
                      :disabled="!editCanConfirm"
                      @click="confirmEdit(term)"
                  >
                    <img src="@/assets/icons/white-checkmark.svg" class="w-3.5 h-3.5" alt="" />
                    Save
                  </button>
                  <button
                      class="text-text-neutrals-placeholder hover:text-text-neutrals-primary cursor-pointer"
                      @click="cancelEdit"
                  >
                    Cancel
                  </button>
                </template>
                <template v-else>
                  <button
                      class="flex items-center gap-1.5 text-text-neutrals-placeholder hover:text-text-neutral-extreme transition-colors cursor-pointer disabled:opacity-40 disabled:cursor-default disabled:hover:text-text-neutrals-placeholder"
                      :disabled="isVocabSyncing"
                      @click="startEdit(term)"
                  >
                    <svg width="16" height="16" viewBox="0 0 19 19" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4">
                      <path d="M13.9167 7.52369L10.5834 4.19036M1 17.107L3.82031 16.7937C4.16488 16.7554 4.33717 16.7362 4.4982 16.6841C4.64108 16.6378 4.77704 16.5725 4.90241 16.4898C5.04371 16.3966 5.16629 16.2741 5.41144 16.0289L16.4167 5.02369C17.3372 4.10322 17.3372 2.61083 16.4167 1.69036C15.4962 0.769882 14.0038 0.769881 13.0834 1.69035L2.07811 12.6956C1.83296 12.9407 1.71038 13.0633 1.6172 13.2046C1.53453 13.33 1.46918 13.4659 1.42293 13.6088C1.3708 13.7699 1.35165 13.9421 1.31337 14.2867L1 17.107Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    Edit
                  </button>
                  <span class="relative inline-flex w-5 h-5 shrink-0">
                    <input
                        type="checkbox"
                        class="peer absolute inset-0 w-5 h-5 opacity-0 cursor-pointer"
                        :checked="selectedIds.has(term.id)"
                        @change="toggleSelect(term.id)"
                    />
                    <span class="w-5 h-5 rounded-md border border-stroke-resting peer-hover:border-text-neutral-extreme peer-checked:bg-elements-blue peer-checked:border-elements-blue transition-colors pointer-events-none"></span>
                    <img src="@/assets/icons/white-checkmark.svg" class="absolute inset-0 m-auto w-3 h-3 opacity-0 peer-checked:opacity-100 pointer-events-none" alt="" />
                  </span>
                </template>
              </div>
            </td>
          </tr>
          </tbody>
        </table>
      </div>

      <!-- Footer -->
      <div class="flex items-center justify-between px-4 py-4 border-t border-stroke-separator text-sm text-text-neutral-medium">
        <span>Showing {{ visibleTerms.length }} of {{ terms.length }} words</span>
      </div>
      </div>
    </div>

    <!-- Delete confirmation -->
    <sheet-modal v-model="confirmDeleteOpen" exact-width="w-[350px]">
      <div class="flex flex-col items-center">
        <img src="@/assets/icons/caution.svg" class="w-14 h-14 mb-7" />
        <h3 class="font-inter text-[19px] font-semibold leading-[30px] mb-2 text-white">{{ confirmDeleteTitle }}</h3>
        <p class="text-sm text-white/75 mb-7 text-center">The selected words will be permanently removed from the vocabulary.</p>
        <div class="flex flex-col w-full">
          <button
              @click="confirmDeleteAction"
              class="self-stretch px-5 py-2.5 bg-[#FF6566] hover:bg-opacity-30 rounded-xl inline-flex justify-center items-center gap-2 overflow-hidden transition duration-160 cursor-pointer"
          >
            <div class="justify-start text-black text-base font-medium font-['Inter'] leading-normal">
              Delete
            </div>
          </button>
          <div @click="confirmDeleteOpen = false" class="text-[#6CBFFF] text-base leading-[1.2rem] font-inter mt-5 mb-2 text-center cursor-pointer">
            Cancel
          </div>
        </div>
      </div>
    </sheet-modal>

  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, inject } from 'vue'
import { useStore } from 'vuex'
import LanguagePicker from '@/components/session/LanguagePicker.vue'
import LanguageSelect from '@/components/ui/LanguageSelect.vue'
import SheetModal from '@/components/ui/SheetModal.vue'
import api from '@/services/api2.service'
import { useLanguages } from '@/composables/useLanguages'
import customVocabularyService from '@/services/custom-vocabulary.service'
import { getAllowedCharSet, getInvalidChars } from '@/services/vocabulary-charset.service'
import { Constants } from '@/cfg/constants'

interface VocabularyTerm {
  id: string
  text: string
  language: string
  displayed_as: string
  pending: boolean
  edited: boolean
}

interface AwsLanguageStatus {
  language: string
  id: string
  status: string
}

const store = useStore()
const organization = computed(() => store.getters['nonpersist/getUserOrganization'])
const organizationId = computed(() => organization.value?.id)
const toast = inject('toast') as { success: Function, error: Function } | undefined

function notifyError(err: any, fallback = 'Something went wrong') {
  const message = err?.response?.data?.error || fallback
  if (toast) toast.error(message)
  else console.error(message)
}

const listId = ref<string | null>(null)
const loading = ref(false)
const terms = ref<VocabularyTerm[]>([])
const selectedIds = ref<Set<string>>(new Set())

const awsIds = ref<AwsLanguageStatus[]>([])
const localSyncOverride = ref(false)
const awsSyncing = computed(() => awsIds.value.some(a => a.status !== 'ready' && a.status !== 'failed'))
const isVocabSyncing = computed(() => localSyncOverride.value || awsSyncing.value)

function mapTerm(raw: any): VocabularyTerm {
  return {
    id: raw.id ?? raw._id,
    text: raw.phrase ?? raw.term ?? '',
    language: raw.language,
    displayed_as: raw.display_as ?? raw.displayed_as ?? raw.phrase ?? raw.term ?? '',
    pending: false,
    edited: false
  }
}

async function fetchTerms() {
  if (!listId.value) return

  const { data } = await customVocabularyService.getTerms(listId.value)
  const result = data?.result
  const rawTerms = Array.isArray(result) ? result : (result?.terms ?? [])
  terms.value = rawTerms.map(mapTerm)
}

async function init() {
  if (!organizationId.value) return

  loading.value = true
  try {
    const { data } = await api.get(`/organization/info/${organizationId.value}`)
    const lists = data?.result?.custom_vocabulary_lists ?? []
    let list = lists[0]

    if (!list) {
      const created = await customVocabularyService.createList('Default Vocabulary', 'organization', organizationId.value)
      list = created.data?.result
    }

    listId.value = list?.id ?? list?._id ?? null
    awsIds.value = list?.aws_ids ?? []
    await fetchTerms()
  } catch (err) {
    notifyError(err, 'Failed to load custom vocabulary')
  } finally {
    loading.value = false
  }
}

const SYNC_POLL_INTERVAL_MS = 60 * 1000
let syncPollTimer: ReturnType<typeof setInterval> | null = null

async function checkSyncStatus() {
  if (!organizationId.value) return

  try {
    const { data } = await api.get(`/organization/info/${organizationId.value}`)
    const list = (data?.result?.custom_vocabulary_lists ?? [])[0]
    awsIds.value = list?.aws_ids ?? []

    if (!awsSyncing.value) {
      localSyncOverride.value = false
    }
  } catch (err) {
    // Keep the previous sync state if the check fails; the next poll will retry.
  }
}

onMounted(() => {
  init()
  syncPollTimer = setInterval(checkSyncStatus, SYNC_POLL_INTERVAL_MS)
})

onUnmounted(() => {
  if (syncPollTimer) clearInterval(syncPollTimer)
})

// ADD WORD FORM
const { getLanguageName } = useLanguages()

const form = ref({
  language: Constants.DEFAULT_LANGUAGE,
  term: '',
  displayed_as: ''
})

const DIGIT_WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine']
const WORD_TO_DIGIT: Record<string, string> = DIGIT_WORDS.reduce((acc, word, digit) => {
  acc[word] = String(digit)
  return acc
}, {} as Record<string, string>)

const termDigits = computed(() => (form.value.term.match(/\d/g) || []).join(''))
const termHasDigits = computed(() => termDigits.value.length > 0)
const termDigitsAsWords = computed(() => termDigits.value.split('').map(d => DIGIT_WORDS[Number(d)]).join('-'))

// CHARACTER SET VALIDATION
const addFormAllowedSet = ref<Set<number> | null>(null)

watch(() => form.value.language, async (lang) => {
  addFormAllowedSet.value = await getAllowedCharSet(lang)
}, { immediate: true })

const termInvalidChars = computed(() => getInvalidChars(form.value.term, addFormAllowedSet.value))
const termInvalidNonDigitChars = computed(() => termInvalidChars.value.filter(c => !/\d/.test(c)))

const canSubmit = computed(() => form.value.term.trim().length > 0 && !termHasDigits.value && termInvalidNonDigitChars.value.length === 0 && !isVocabSyncing.value)

const justEnabled = ref(false)

watch(canSubmit, (enabled, wasEnabled) => {
  if (enabled && !wasEnabled) {
    justEnabled.value = true
    setTimeout(() => { justEnabled.value = false }, 350)
  }
})

let tempIdCounter = 0

function nextTempId(): string {
  tempIdCounter += 1
  return `temp-${tempIdCounter}`
}

function deriveDisplayedAs(text: string): string {
  const tokens = text.split('-')
  const result: string[] = []
  let i = 0

  while (i < tokens.length) {
    const digit = WORD_TO_DIGIT[tokens[i].toLowerCase()]
    if (digit !== undefined) {
      let digits = digit
      i += 1
      while (i < tokens.length && WORD_TO_DIGIT[tokens[i].toLowerCase()] !== undefined) {
        digits += WORD_TO_DIGIT[tokens[i].toLowerCase()]
        i += 1
      }
      result.push(digits)
    } else {
      result.push(tokens[i])
      i += 1
    }
  }

  return result.join(' ')
}

function addWord() {
  if (!canSubmit.value || loading.value) return

  const rawTerm = form.value.term.trim()
  const text = rawTerm.replace(/\s+/g, '-')

  terms.value.unshift({
    id: nextTempId(),
    text,
    language: form.value.language,
    displayed_as: form.value.displayed_as.trim() || deriveDisplayedAs(text),
    pending: true,
    edited: false
  })

  form.value.term = ''
  form.value.displayed_as = ''
}

// BULK IMPORT
const MAX_BULK_WORDS = 100

interface BulkImportState {
  file: File | null
  status: 'idle' | 'uploading' | 'success' | 'error'
  progress: number
  loadedBytes: number
  message: string
}

const csvInputRef = ref<HTMLInputElement | null>(null)
const dragOver = ref(false)
const bulkImport = ref<BulkImportState>({ file: null, status: 'idle', progress: 0, loadedBytes: 0, message: '' })

const bulkImportBorderClass = computed(() => {
  if (bulkImport.value.status === 'success') return 'border-[#3DDC84] bg-[#3DDC84]/5'
  if (bulkImport.value.status === 'error') return 'border-elements-red bg-elements-red/5'
  if (bulkImport.value.status === 'uploading') return 'border-elements-blue bg-layer-0'
  if (dragOver.value) return 'border-elements-blue bg-layer-2'
  return 'border-stroke-separator bg-layer-0 hover:border-elements-blue/50'
})

const bulkImportStatusTextClass = computed(() => {
  if (bulkImport.value.status === 'success') return 'text-[#3DDC84]'
  if (bulkImport.value.status === 'error') return 'text-elements-red'
  return 'text-elements-blue'
})

const bulkImportBarClass = computed(() => {
  if (bulkImport.value.status === 'success') return 'bg-[#3DDC84]'
  if (bulkImport.value.status === 'error') return 'bg-elements-red'
  return 'bg-elements-blue'
})

const bulkImportStatusText = computed(() => {
  const b = bulkImport.value
  if (b.status === 'uploading') return 'Uploading...'
  return b.message
})

function formatBytes(bytes: number): string {
  const mb = bytes / (1024 * 1024)
  return `${mb.toFixed(1)} MB`
}

function onDropzoneClick() {
  if (bulkImport.value.file || isVocabSyncing.value) return
  csvInputRef.value?.click()
}

function onFileInputChange(e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  if (file) handleCsvFile(file)
  input.value = ''
}

function onFileDrop(e: DragEvent) {
  dragOver.value = false
  const file = e.dataTransfer?.files?.[0]
  if (file) handleCsvFile(file)
}

function resetBulkImport() {
  bulkImport.value = { file: null, status: 'idle', progress: 0, loadedBytes: 0, message: '' }
}

function handleCsvFile(file: File) {
  if (isVocabSyncing.value) return

  const isCsv = file.name.toLowerCase().endsWith('.csv') || file.type === 'text/csv'

  if (!isCsv) {
    bulkImport.value = { file, status: 'error', progress: 100, loadedBytes: file.size, message: 'Please upload a .csv file.' }
    return
  }

  bulkImport.value = { file, status: 'uploading', progress: 0, loadedBytes: 0, message: '' }

  const reader = new FileReader()

  reader.onerror = () => {
    bulkImport.value = { ...bulkImport.value, status: 'error', progress: 100, message: 'Failed to read the file.' }
  }

  reader.onload = () => {
    const text = String(reader.result || '')
    animateBulkImportProgress(file, async () => {
      const outcome = await parseBulkImportCsv(text)
      bulkImport.value = {
        ...bulkImport.value,
        status: outcome.status,
        progress: 100,
        loadedBytes: file.size,
        message: outcome.message
      }
    })
  }

  reader.readAsText(file)
}

function animateBulkImportProgress(file: File, onDone: () => void) {
  const durationMs = 700
  const startedAt = performance.now()

  function step(now: number) {
    const elapsed = now - startedAt
    const progress = Math.min(99, Math.round((elapsed / durationMs) * 100))
    bulkImport.value = { ...bulkImport.value, progress, loadedBytes: Math.round((progress / 100) * file.size) }

    if (elapsed < durationMs) {
      requestAnimationFrame(step)
    } else {
      onDone()
    }
  }

  requestAnimationFrame(step)
}

function isHeaderRow(line: string): boolean {
  const secondColumn = line.split(',')[1]?.trim().toLowerCase()
  return secondColumn === 'phrase' || secondColumn === 'term'
}

async function parseBulkImportCsv(text: string): Promise<{ status: 'success' | 'error', message: string }> {
  const lines = text.split(/\r\n|\n|\r/).map(l => l.trim()).filter(l => l.length > 0)

  if (lines.length && isHeaderRow(lines[0])) {
    lines.shift()
  }

  if (!lines.length) {
    return { status: 'error', message: 'The file is empty.' }
  }

  if (lines.length > MAX_BULK_WORDS) {
    return { status: 'error', message: `The file contains ${lines.length} words, which exceeds the ${MAX_BULK_WORDS} word limit.` }
  }

  const parsedRows = lines
      .map(line => {
        const [langRaw, termRaw, displayedRaw] = line.split(',')
        const rawTerm = (termRaw ?? '').trim()
        const language = (langRaw ?? '').trim() || Constants.DEFAULT_LANGUAGE
        const displayedRawTrimmed = (displayedRaw ?? '').trim()
        return { language, rawTerm, displayedRawTrimmed }
      })
      .filter(row => row.rawTerm.length > 0)

  if (!parsedRows.length) {
    return { status: 'error', message: 'No valid words were found in the file.' }
  }

  const skippedDigits = parsedRows.filter(row => /\d/.test(row.rawTerm)).length
  const nonDigitRows = parsedRows.filter(row => !/\d/.test(row.rawTerm))

  const languagesInFile = [...new Set(nonDigitRows.map(row => row.language))]
  const allowedSets = await Promise.all(languagesInFile.map(lang => getAllowedCharSet(lang)))
  const allowedSetByLanguage = new Map(languagesInFile.map((lang, i) => [lang, allowedSets[i]]))

  const skippedInvalidChars = nonDigitRows.filter(
      row => getInvalidChars(row.rawTerm, allowedSetByLanguage.get(row.language) ?? null).length > 0
  ).length

  const rows = nonDigitRows
      .filter(row => getInvalidChars(row.rawTerm, allowedSetByLanguage.get(row.language) ?? null).length === 0)
      .map(row => {
        const text = row.rawTerm.replace(/\s+/g, '-')
        return {
          language: row.language,
          text,
          displayed_as: row.displayedRawTrimmed || deriveDisplayedAs(text)
        }
      })

  if (!rows.length) {
    return { status: 'error', message: 'None of the rows contained a Term valid for its language — check for numbers or unsupported characters.' }
  }

  rows.forEach(row => {
    terms.value.unshift({
      id: nextTempId(),
      text: row.text,
      language: row.language,
      displayed_as: row.displayed_as,
      pending: true,
      edited: false
    })
  })

  const skippedParts: string[] = []
  if (skippedDigits > 0) skippedParts.push(`${skippedDigits} row${skippedDigits === 1 ? '' : 's'} with numbers`)
  if (skippedInvalidChars > 0) skippedParts.push(`${skippedInvalidChars} row${skippedInvalidChars === 1 ? '' : 's'} with characters unsupported for their language`)
  const skippedMessage = skippedParts.length ? ` Skipped: ${skippedParts.join(', ')}.` : ''

  return { status: 'success', message: `Upload complete! ${rows.length} words added.${skippedMessage}` }
}

// SORT & SEARCH
const sortMode = ref<'new' | 'alphabetical' | 'language'>('new')
const searchQuery = ref('')

const sortOptions = [
  { value: 'new', label: 'New' },
  { value: 'alphabetical', label: 'Alphabetical' },
  { value: 'language', label: 'Language' }
] as const

const filteredTerms = computed(() => {
  const q = searchQuery.value.trim().toLowerCase()
  if (!q) return terms.value
  return terms.value.filter(t => t.text.toLowerCase().includes(q) || t.displayed_as.toLowerCase().includes(q))
})

const visibleTerms = computed(() => {
  if (sortMode.value === 'alphabetical') {
    return [...filteredTerms.value].sort((a, b) => a.text.localeCompare(b.text))
  }
  if (sortMode.value === 'language') {
    return [...filteredTerms.value].sort((a, b) => getLanguageName(a.language).localeCompare(getLanguageName(b.language)))
  }
  // 'new': the API has no created-date field, so this is just insertion order —
  // newly-added pending terms are already unshifted to the front.
  return filteredTerms.value
})

function toggleSelect(id: string) {
  const next = new Set(selectedIds.value)
  if (next.has(id)) {
    next.delete(id)
  } else {
    next.add(id)
  }
  selectedIds.value = next
}

async function deleteSelected() {
  const ids = Array.from(selectedIds.value)
  const savedIds = ids.filter(id => !terms.value.find(t => t.id === id)?.pending)

  try {
    if (savedIds.length) {
      await customVocabularyService.deleteTerms(savedIds)
      localSyncOverride.value = true
    }
    terms.value = terms.value.filter(t => !selectedIds.value.has(t.id))
    selectedIds.value = new Set()
  } catch (err) {
    notifyError(err, 'Failed to delete terms')
  }
}

// INLINE EDIT
const editingId = ref<string | null>(null)
const editDraft = ref({ text: '', displayed_as: '', language: '' })
const editLanguageSelectorOpen = ref(false)
const editLanguageBtnRef = ref<HTMLElement | HTMLElement[] | null>(null)
const editDropdownPos = ref({ left: 0, top: 0, bottom: 0, openUp: false })

const editAllowedSet = ref<Set<number> | null>(null)

watch(() => editDraft.value.language, async (lang) => {
  editAllowedSet.value = lang ? await getAllowedCharSet(lang) : null
}, { immediate: true })

const editHasDigits = computed(() => editingId.value !== null && /\d/.test(editDraft.value.text))
const editInvalidChars = computed(() => editingId.value === null ? [] : getInvalidChars(editDraft.value.text, editAllowedSet.value))
const editInvalidNonDigitChars = computed(() => editInvalidChars.value.filter(c => !/\d/.test(c)))
const editCanConfirm = computed(() => editDraft.value.text.trim().length > 0 && !editHasDigits.value && editInvalidNonDigitChars.value.length === 0)

const editDropdownStyle = computed(() => {
  const pos = editDropdownPos.value
  return pos.openUp
      ? { left: `${pos.left}px`, bottom: `${pos.bottom}px` }
      : { left: `${pos.left}px`, top: `${pos.top}px` }
})

function startEdit(term: VocabularyTerm) {
  if (isVocabSyncing.value) return
  editingId.value = term.id
  editDraft.value = { text: term.text, displayed_as: term.displayed_as, language: term.language }
  editLanguageSelectorOpen.value = false
}

function cancelEdit() {
  editingId.value = null
  editLanguageSelectorOpen.value = false
}

function toggleEditLanguageSelector() {
  const btn = Array.isArray(editLanguageBtnRef.value) ? editLanguageBtnRef.value[0] : editLanguageBtnRef.value

  if (!editLanguageSelectorOpen.value && btn) {
    const rect = btn.getBoundingClientRect()
    editDropdownPos.value = { left: rect.left, top: 0, bottom: window.innerHeight - rect.top + 8, openUp: true }
  }
  editLanguageSelectorOpen.value = !editLanguageSelectorOpen.value
}

function onSelectEditLanguage(code: string) {
  editDraft.value.language = code
  editLanguageSelectorOpen.value = false
}

function confirmEdit(term: VocabularyTerm) {
  if (isVocabSyncing.value || !editCanConfirm.value) return

  const text = editDraft.value.text.trim()

  term.text = text
  term.displayed_as = editDraft.value.displayed_as.trim() || text
  term.language = editDraft.value.language
  if (!term.pending) term.edited = true

  editingId.value = null
  editLanguageSelectorOpen.value = false
}

// DELETE CONFIRMATION
const confirmDeleteOpen = ref(false)

function requestDeleteSelected() {
  if (selectedIds.value.size === 0 || isVocabSyncing.value) return
  confirmDeleteOpen.value = true
}

const confirmDeleteTitle = computed(() => {
  const count = selectedIds.value.size
  return `Delete ${count} selected word${count === 1 ? '' : 's'}?`
})

async function confirmDeleteAction() {
  confirmDeleteOpen.value = false
  await deleteSelected()
}

// SAVE
const hasUnsavedChanges = computed(() => terms.value.some(t => t.pending || t.edited))
const saving = ref(false)

async function saveChanges() {
  if (!hasUnsavedChanges.value || !listId.value || saving.value || isVocabSyncing.value) return

  const pendingTerms = terms.value
      .filter(t => t.pending)
      .map(t => ({ phrase: t.text, display_as: t.displayed_as, language: t.language }))

  const editedTerms = terms.value
      .filter(t => t.edited && !t.pending)
      .map(t => ({ id: t.id, phrase: t.text, display_as: t.displayed_as, language: t.language }))

  saving.value = true
  try {
    if (pendingTerms.length) {
      await customVocabularyService.addTerms(listId.value, pendingTerms)
    }
    if (editedTerms.length) {
      await customVocabularyService.updateTerms(editedTerms)
    }
    localSyncOverride.value = true
    await fetchTerms()
    selectedIds.value = new Set()
    if (toast) toast.success('Vocabulary saved')
  } catch (err) {
    notifyError(err, 'Failed to save vocabulary')
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
@keyframes add-btn-pop {
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(108, 191, 255, 0.5); }
  40% { transform: scale(1.08); box-shadow: 0 0 0 6px rgba(108, 191, 255, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(108, 191, 255, 0); }
}

.add-btn-pop {
  animation: add-btn-pop 0.35s ease-out;
}
</style>
